import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { reportNumber, reportDistribution } from '../../utils/rivalReport';

const OUTCOMES = ['win', 'draw', 'loss'];

function OutcomeBreakdown({ values, played, halftime = false, missingValue = '—' }) {
  const { t, i18n } = useTranslation('reports');
  const { counts, shares } = reportDistribution(values, played);
  const percent = value => value.toLocaleString(i18n.language, { style: 'percent', maximumFractionDigits: 1 });
  return <div className="rr-outcome-breakdown">
    {shares && <div className="rr-outcome-track" aria-hidden="true">
      {shares.map((share, index) => <span key={OUTCOMES[index]} className={`rr-tone-${OUTCOMES[index]}`}
        style={{ width: `${share * 100}%` }} />)}
    </div>}
    <dl className="rr-outcome-values">
      {OUTCOMES.map((outcome, index) => <div className={`rr-tone-${outcome}`} key={outcome}>
        <dt><i aria-hidden="true" />{t(halftime
          ? ['stats.winning', 'stats.drawing', 'stats.losing'][index]
          : ['stats.won', 'stats.drawn', 'stats.lost'][index])}</dt>
        <dd aria-label={t(halftime
          ? ['stats.winning', 'stats.drawing', 'stats.losing'][index]
          : ['stats.won', 'stats.drawn', 'stats.lost'][index])}>{counts[index] === null ? missingValue : counts[index].toLocaleString(i18n.language)}
          {shares && <small>{percent(shares[index])}</small>}</dd>
      </div>)}
    </dl>
    {!shares && <p className="rr-distribution-note">{t(reportNumber(played) === 0
      ? 'modern.noRecordedMatches' : 'modern.breakdownUnavailable')}</p>}
  </div>;
}

export function ReportResultCard({ title, stats, venue, missingValue = '—' }) {
  const { t, i18n } = useTranslation('reports');
  const titleId = useId();
  const played = reportNumber(stats.jugados);
  const outcomes = [stats.ganados, stats.empatados, stats.perdidos];
  const { shares } = reportDistribution(outcomes, played);
  const format = value => reportNumber(value)?.toLocaleString(i18n.language) ?? missingValue;
  const Icon = venue === 'home' ? HomeOutlinedIcon : venue === 'away' ? NorthEastIcon : AssessmentOutlinedIcon;
  return <section className={`rr-panel rr-performance-card ${venue ? '' : 'rr-performance-overall'}`} aria-labelledby={titleId}>
    <header className="rr-performance-heading">
      <div><span className="rr-eyebrow">{t('modern.resultsEyebrow')}</span><h2 id={titleId}>{title}</h2></div>
      <Icon aria-hidden="true" />
    </header>
    <dl className="rr-performance-hero">
      <div><dt>{t('stats.played')}</dt><dd>{format(played)}</dd></div>
      <div><dt>{t('modern.winShare')}</dt><dd>{shares
        ? shares[0].toLocaleString(i18n.language, { style: 'percent', maximumFractionDigits: 1 }) : missingValue}</dd></div>
    </dl>
    <OutcomeBreakdown values={outcomes} played={played} missingValue={missingValue} />
    <dl className="rr-performance-goals">
      {[
        ['stats.goalsFor', stats.golesFavor, SportsSoccerIcon, 'win'],
        ['stats.goalsAgainst', stats.golesContra, ShieldOutlinedIcon, 'loss'],
      ].map(([label, value, GoalIcon, tone]) => <div key={label} className={`rr-tone-${tone}`}>
        <dt><GoalIcon aria-hidden="true" />{t(label)}</dt><dd aria-label={t(label)}>{format(value)}</dd>
      </div>)}
    </dl>
  </section>;
}

export function ReportHalftimeCard({ matches, missingValue = '—' }) {
  const { t } = useTranslation('reports');
  const titleId = useId();
  const rows = [
    ['stats.total', 'Total', 'partidosJugados'],
    ['stats.local', 'Local', 'partidosJugadosLocal'],
    ['stats.visitante', 'Visitante', 'partidosJugadosVisitante'],
  ];
  return <section className="rr-panel rr-halftime-card" aria-labelledby={titleId}>
    <header className="rr-performance-heading">
      <div><span className="rr-eyebrow">{t('modern.halftimeEyebrow')}</span>
        <h2 id={titleId}>{t('sections.halftimeState')}</h2></div>
      <span className="rr-halftime-clock" aria-hidden="true">45<span>′</span></span>
    </header>
    <p className="rr-performance-help">{t('modern.halftimeHelp')}</p>
    <div className="rr-halftime-grid">
      {rows.map(([label, suffix, sample]) => {
        const played = reportNumber(matches[sample]);
        return <section className="rr-halftime-scope" key={suffix} aria-label={t(label)}>
          <header><h3>{t(label)}</h3><span>{played === null ? t('modern.sampleUnknown')
            : t('modern.sample', { count: played })}</span></header>
          <OutcomeBreakdown halftime played={played} missingValue={missingValue}
            values={['Ganando', 'Empatando', 'Perdiendo'].map(state => matches[`descanso${state}${suffix}`])} />
        </section>;
      })}
    </div>
  </section>;
}

export function ReportCleanSheets({ matches }) {
  const { t, i18n } = useTranslation('reports');
  const titleId = useId();
  if (!matches) return null;
  const format = value => value.toLocaleString(i18n.language);
  return <section className="rr-panel rr-clean-sheets" aria-labelledby={titleId}>
    <header className="rr-performance-heading"><div><span className="rr-eyebrow">{t('modern.defenceEyebrow')}</span>
      <h2 id={titleId}>{t('sections.cleanSheets')}</h2></div><ShieldOutlinedIcon aria-hidden="true" /></header>
    <p className="rr-performance-help">{t('modern.cleanSheetsHelp')}</p>
    <div className="rr-clean-grid">{[['Final', 'stats.atFullTime', '90'], ['Descanso', 'stats.atHalftime', '45']]
      .map(([suffix, label, minute]) => {
        const home = reportNumber(matches[`porteria0${suffix}Local`]) ?? 0;
        const away = reportNumber(matches[`porteria0${suffix}Visitante`]) ?? 0;
        const total = home + away;
        return <section key={suffix} className="rr-clean-scope" aria-label={t(label)}>
          <header><h3>{t(label)}</h3><span aria-hidden="true">{minute}′</span></header>
          <dl className="rr-clean-total"><dt>{t('stats.total')}</dt><dd>{format(total)}</dd></dl>
          {total > 0 && <div className="rr-clean-track" aria-hidden="true">
            <span style={{ width: `${home / total * 100}%` }} />
            <span style={{ width: `${away / total * 100}%` }} />
          </div>}
          <dl className="rr-clean-venues">{[['stats.local', home], ['stats.visitante', away]].map(([key, count]) =>
            <div key={key}><dt>{t(key)}</dt><dd>{format(count)}</dd></div>)}</dl>
        </section>;
      })}</div>
  </section>;
}

export function ReportStreaks({ standing }) {
  const { t } = useTranslation('reports');
  const titleId = useId();
  const groups = [
    ['stats.general', key => !/_(casa|fuera)$/i.test(key)],
    ['stats.home', key => /_casa$/i.test(key)],
    ['stats.away', key => /_fuera$/i.test(key)],
  ].map(([label, filter]) => ({
    label,
    keys: Object.keys(standing || {}).filter(key => key.startsWith('racha_') && filter(key))
      .sort((a, b) => Number(a.match(/\d+/)?.[0] || 0) - Number(b.match(/\d+/)?.[0] || 0)),
  })).filter(group => group.keys.length);
  if (!groups.length) return null;
  return <section className="rr-panel rr-streaks" aria-labelledby={titleId}>
    <header className="rr-performance-heading"><div><span className="rr-eyebrow">{t('modern.context.form')}</span>
      <h2 id={titleId}>{t('sections.streaks')}</h2></div><TrendingUpIcon aria-hidden="true" /></header>
    <p className="rr-performance-help">{t('modern.streaksHelp')}</p>
    {groups.map(group => <section key={group.label} className="rr-streak-group" aria-label={t(group.label)}>
      <h3>{t(group.label)}</h3><ol className="rr-streak-list">{group.keys.map(key => {
        const value = standing[key];
        const outcome = { G: 'win', E: 'draw', P: 'loss' }[String(value).toUpperCase()];
        const label = key.replace(/^racha_/, '').replace(/_casa$|_fuera$/i, '').replace(/_/g, ' ');
        return <li key={key} className={`rr-streak-item ${outcome ? `rr-tone-${outcome}` : ''}`}>
          <strong aria-label={outcome ? t(`modern.context.${outcome}`) : undefined}>
            {outcome ? t(`modern.outcomes.${outcome}`) : String(value ?? '—')}</strong><span>{label}</span>
        </li>;
      })}</ol>
    </section>)}
  </section>;
}
